import {
  differenceInMinutes,
  differenceInYears,
  intervalToDuration,
  isValid,
  parse,
  setSeconds,
  startOfDay,
  subDays,
  subMinutes,
  subMonths,
} from 'date-fns';
import { ru } from 'date-fns/locale';
import {
  format,
  formatInTimeZone,
  utcToZonedTime as utcToLocal,
  utcToZonedTime,
} from 'date-fns-tz';

import {
  DATE_FORMAT,
  HH_MM_TIME_FORMAT,
  ISO_DATE_TIME_FORMAT,
} from '@config/format';

export {
  addDays,
  differenceInCalendarDays,
  differenceInMilliseconds,
  differenceInMinutes,
  getDay,
  getDaysInMonth,
  isAfter,
  isBefore,
  subYears,
} from 'date-fns';
export { utcToZonedTime } from 'date-fns-tz';

export const getTimezoneOffset = (): number => {
  return new Date().getTimezoneOffset();
};

export const calculateAgeFromBirthDate = (
  birthDate: undefined | string,
  timezoneOffset = getTimezoneOffset()
): undefined | number => {
  if (!birthDate) {
    return undefined;
  }

  const withTimezoneOffset = timezoneOffset
    ? subMinutes(new Date(birthDate), timezoneOffset)
    : new Date(birthDate);

  return differenceInYears(new Date(), withTimezoneOffset);
};

export const calculateElapsedTime = (
  startDate: undefined | string,
  timezoneOffset = getTimezoneOffset()
): undefined | number => {
  if (!startDate) {
    return undefined;
  }

  const withTimezoneOffset = timezoneOffset
    ? subMinutes(new Date(startDate), timezoneOffset)
    : new Date(startDate);

  return differenceInMinutes(new Date(), withTimezoneOffset);
};

export function toValidDate(value: undefined | string): undefined | Date {
  if (!value) {
    return undefined;
  }

  const date = new Date(value);
  if (!isValid(date)) {
    return undefined;
  }

  return date;
}

export const parseDateByFormat = (
  value: string | undefined,
  dateFormat: string
): Date | undefined => {
  if (!value) {
    return undefined;
  }

  const date = parse(value, dateFormat, new Date());

  return isValid(date) ? date : undefined;
};

export const formatDate = (
  date: Date,
  dateFormat = DATE_FORMAT,
  options = {
    locale: ru,
    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  }
): string => {
  const localTime = utcToLocal(date, options.timeZone);

  return format(localTime, dateFormat, options);
};

export const formatDateInUTC = (
  date: Date,
  dateFormat = DATE_FORMAT
): string => {
  const utcDate = utcToZonedTime(date, 'UTC');

  return format(utcDate, dateFormat, { timeZone: 'UTC' });
};

export const enforceDateStringToISO = (date: string): string =>
  date.includes('Z') ? date : `${date}Z`;

export const getCurrentYear = (): number => new Date().getFullYear();

export const getLocalizedInterval = (
  interval: Duration,
  timeLocalizedLabels: string[] = ['г', 'м', 'д', 'ч', 'м', 'с']
): string =>
  Object.values(interval)
    .map((value, index) => {
      return value //
        ? `${value}${timeLocalizedLabels[index]}`
        : null;
    })
    .join(' ');

export const localizedDifferenceBetweenTwoDates = (
  start: Date,
  end: Date
): string => {
  const interval = intervalToDuration({ start, end });

  return getLocalizedInterval(interval);
};

export const convertSecondsToLocalizedDuration = (
  seconds: undefined | number
): string => {
  if (!seconds) {
    return '0';
  }

  const start = 0;
  const oneSecondInMs = 1000;
  const interval = intervalToDuration({ start, end: seconds * oneSecondInMs });

  return getLocalizedInterval(interval);
};

export const getDateISOStringWithoutTimezoneOffset = (date: Date): string => {
  return formatDate(date, ISO_DATE_TIME_FORMAT);
};

export const getDateISOStringInTimeZone = (
  date: Date,
  timeZone: string
): string => formatInTimeZone(date, timeZone, ISO_DATE_TIME_FORMAT);

export const getDateOnlyISOStringInTimeZone = (
  date: Date,
  timeZone: string
): string => formatInTimeZone(date, timeZone, DATE_FORMAT);

export const getTimeInTimeZone = (date: Date, timeZone: string): string =>
  formatInTimeZone(date, timeZone, HH_MM_TIME_FORMAT);

export const parseTimeToMinutes = (value: string): number | undefined => {
  if (!/^([01]\d|2[0-3]):[0-5]\d$/.test(value)) {
    return undefined;
  }

  const [hours, minutes] = value.split(':').map(Number);

  return hours * 60 + minutes;
};

export const getCurrentDate = (): Date => {
  return new Date(Date.now());
};

export const getFormattedCurrentDate = (format = DATE_FORMAT): string => {
  return formatDate(getCurrentDate(), format);
};

export const getDateFromDayAgo = (): Date => {
  return subDays(Date.now(), 1);
};

export const isValidDate = (date: Date | null | undefined): boolean => {
  return isValid(date);
};

export const setZeroSeconds = (date: Date): Date => {
  return setSeconds(date, 0);
};

export const getStartOfDayFromMonthsAgo = (
  monthsAgo: number,
  date: Date = new Date()
): Date => {
  return startOfDay(subMonths(date, monthsAgo));
};

export const getTodayInTimeZone = (timeZone: string): Date => {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  })
    .formatToParts(new Date())
    .reduce<Record<string, string>>(
      (result, part) => Object.assign(result, { [part.type]: part.value }),
      {}
    );

  return new Date(
    Number(parts.year),
    Number(parts.month) - 1,
    Number(parts.day)
  );
};
